import { useMemo, useState } from "react";
import { Box, Chip, useTheme } from "@mui/material";
import { DataTable, type Column } from "@/components/DataTable";
import type { Indicador } from "@/components/Indicadores";
import { RelatorioLayout, visoesComGrade, type VisaoRelatorio } from "@/components/escala/RelatorioLayout";
import { FiltroRelatorio } from "@/components/escala/RelatorioFiltroCampos";
import { RelatorioGradeMensal, type LinhaGrade } from "@/components/escala/RelatorioGradeMensal";
import { SeloTipo } from "@/components/escala/EspelhoLinha";
import { EspelhoLegenda } from "@/components/escala/EspelhoLegenda";
import { useRelatorio } from "@/components/escala/useRelatorio";
import { contarPor, distintos, formatNumero, maisFrequente, plural, rotuloData, useColunasDoMes } from "@/components/escala/relatorio";
import { estilosDoTema, rotuloDoTipo } from "@/components/escala/tiposDia";
import { formatDataBR, relFolgas, rotuloLoja, type Loja, type LinhaFolga } from "@/api/escala.service";

const TIPO_LABEL: Record<LinhaFolga["tipo"], string> = { F: "Folga", FA: "Folga de feriado" };

const VISOES = visoesComGrade("Grade");

const porNomeEData = (a: LinhaFolga, b: LinhaFolga) => a.nome.localeCompare(b.nome) || a.data.localeCompare(b.data);

interface LinhaColaborador extends LinhaGrade {
  dias: Map<string, LinhaFolga["tipo"]>;
  folgas: number;
  feriado: number;
}

// Uma linha por colaborador e loja: quem trocou de loja no mês aparece nas duas.
function agruparPorColaborador(linhas: LinhaFolga[], lojas: Loja[]): LinhaColaborador[] {
  const grupos = new Map<string, LinhaColaborador & { escalas: Set<string>; matricula?: string; lojaId: string }>();
  for (const l of linhas) {
    const chave = `${l.colaborador_id}|${l.loja_id}`;
    const grupo = grupos.get(chave) ?? {
      id: chave,
      titulo: l.nome,
      dias: new Map(),
      folgas: 0,
      feriado: 0,
      escalas: new Set<string>(),
      matricula: l.matricula,
      lojaId: l.loja_id,
    };
    grupo.dias.set(l.data, l.tipo);
    if (l.tipo === "F") grupo.folgas++;
    else grupo.feriado++;
    grupo.escalas.add(l.escala_nome);
    grupos.set(chave, grupo);
  }
  return [...grupos.values()]
    .map(({ escalas, matricula, lojaId, ...g }) => ({
      ...g,
      subtitulo: [matricula ? `Mat. ${matricula}` : null, rotuloLoja(lojas, lojaId), ...escalas].filter(Boolean).join(" · "),
    }))
    .sort((a, b) => a.titulo.localeCompare(b.titulo));
}

function indicadoresFolgas(linhas: LinhaFolga[]): [Indicador, Indicador, Indicador, Indicador] {
  const folgas = linhas.filter((l) => l.tipo === "F").length;
  const colaboradores = distintos(linhas, (l) => l.colaborador_id);
  const pico = maisFrequente(linhas, (l) => l.data);
  return [
    { titulo: "Folgas no mês", valor: folgas, nota: `mais ${plural(linhas.length - folgas, "folga")} de feriado` },
    { titulo: "Colaboradores com folga", valor: colaboradores, nota: `em ${plural(distintos(linhas, (l) => l.loja_id), "loja")}` },
    {
      titulo: "Média por colaborador",
      valor: colaboradores ? formatNumero(linhas.length / colaboradores) : "—",
      nota: "folgas e folgas de feriado",
    },
    { titulo: "Dia com mais folgas", valor: pico ? pico[1] : "—", nota: pico ? rotuloData(pico[0]) : "nenhuma folga no mês" },
  ];
}

// Folgas (F e FA) do mês em grade por colaborador, no formato do espelho.
export function EscalaRelFolgasTab() {
  const theme = useTheme();
  const [visao, setVisao] = useState<VisaoRelatorio>("grade");
  const r = useRelatorio({ buscar: relFolgas, ordenar: porNomeEData, mensagemErro: "Erro ao carregar o relatório de folgas" });

  const colunasDia = useColunasDoMes(r.filtros.ano, r.filtros.mes, r.filtros.loja_id, r.clientId);
  const estilos = useMemo(() => estilosDoTema(theme), [theme]);
  const porColaborador = useMemo(() => agruparPorColaborador(r.linhas, r.lojas), [r.linhas, r.lojas]);
  const folgandoPorDia = useMemo(() => contarPor(r.linhas, (l) => l.data), [r.linhas]);
  const indicadores = useMemo(() => indicadoresFolgas(r.linhas), [r.linhas]);
  const totalFolgas = porColaborador.reduce((acc, c) => acc + c.folgas, 0);
  const totalFeriado = porColaborador.reduce((acc, c) => acc + c.feriado, 0);

  const columns: Column<LinhaFolga>[] = [
    { key: "matricula", label: "Matrícula" },
    { key: "nome", label: "Nome" },
    { key: "loja_id", label: "Loja", render: (l) => rotuloLoja(r.lojas, l.loja_id), exportValue: (l) => rotuloLoja(r.lojas, l.loja_id) },
    { key: "escala_nome", label: "Escala" },
    { key: "data", label: "Data", render: (l) => formatDataBR(l.data), exportValue: (l) => formatDataBR(l.data) },
    { key: "tipo", label: "Tipo", render: (l) => <Chip size="small" label={TIPO_LABEL[l.tipo]} />, exportValue: (l) => TIPO_LABEL[l.tipo] },
  ];

  return (
    <RelatorioLayout
      carregando={r.carregando}
      filtro={<FiltroRelatorio estado={r} />}
      indicadores={indicadores}
      exportacao={{ colunas: columns, linhas: r.ordenadas, arquivo: "folgas", aba: "Folgas" }}
      competencia={r.filtros}
      visoes={VISOES}
      visao={visao}
      onVisao={setVisao}
      legenda={visao === "grade" && <EspelhoLegenda tipos={["F", "FA"]} />}
    >
      {visao === "lista" ? (
        <DataTable columns={columns} {...r.tabela} />
      ) : (
        <RelatorioGradeMensal
          colunas={colunasDia}
          linhas={porColaborador}
          rotulo="Colaborador"
          celula={(colaborador, c) => {
            const tipo = colaborador.dias.get(c.iso);
            if (!tipo) return null;
            return {
              titulo: `${formatDataBR(c.iso)}${c.feriado ? ` — ${c.feriado}` : ""} — ${rotuloDoTipo(tipo)}`,
              conteudo: <SeloTipo tipo={tipo} estilo={estilos[tipo]!} />,
            };
          }}
          totais={[
            { rotulo: "F", titulo: "Folgas", valor: (l) => <Box sx={{ color: "error.main" }}>{l.folgas}</Box> },
            { rotulo: "FA", titulo: "Folgas de feriado", valor: (l) => l.feriado || "" },
          ]}
          rodape={{
            titulo: "Folgando no dia",
            celula: (c) => folgandoPorDia.get(c.iso) || "",
            totais: [totalFolgas, totalFeriado || ""],
          }}
        />
      )}
    </RelatorioLayout>
  );
}
